import { useTranslation } from "react-i18next";
import { DataTable } from "../components/table/DataTable";
import { useCallback, useMemo, useState } from "react";
import Form from "../components/form/Form";
import { Modal } from "../components/modal/Modal";
import { useQuery } from "@tanstack/react-query";
import { useTrainingRoute } from "../../core/hooks/useTrainingRoute";
import { useAdminPermissions } from "../../core/hooks/useAdminPermissions";
import trainingVersionTableConfig from "./config/trainingVersion/trainingVersionTableConfig";
import trainingVersionFormConfig from "./config/trainingVersion/trainingVersionFormConfig";
import PageLayout from "../components/layout/PageLayout";
import {
  emptyTrainingVersion,
  TrainingVersion,
  TrainingVersionFormData,
  trainingVersionToFormData,
} from "../../core/trainingVersion/entities/TrainingVersion";
import { useTrainingVersionFormValidation } from "../../core/hooks/form/useTrainingVersionFormValidation";
import { getAllTrainingVersionsUseCase } from "../../core/trainingVersion/useCases/getAllTrainingVersionsUseCase";
import { deleteTrainingVersionUseCase } from "../../core/trainingVersion/useCases/deleteTrainingVersionUseCase";
import { addUpdateTrainingVersionUseCase } from "../../core/trainingVersion/useCases/addUpdateTrainingVersionUseCase";
import { updateTrainingVersionOrderUseCase } from "../../core/trainingVersion/useCases/updateTrainingVersionOrderUseCase";
import { getAllTopicsUseCase } from "../../core/topic/useCases/getAllTopicsUseCase";
import { HistoryButton } from "../components/history/HistoryButton";

export default function TrainingVersionPage() {
  const { t } = useTranslation();
  const { trainingId } = useTrainingRoute();
  const permissions = useAdminPermissions();

  const [selected, setSelected] = useState<
    TrainingVersion | TrainingVersionFormData | null
  >(null);

  const formValues = useMemo(
    () => (selected ? trainingVersionToFormData(selected) : null),
    [selected]
  );
  const form = useTrainingVersionFormValidation(formValues, t);

  const { data: topics = [] } = useQuery({
    queryKey: ["topics", trainingId],
    queryFn: () => getAllTopicsUseCase.exec(trainingId!),
    enabled: !!trainingId,
  });

  const topicOptions = useMemo(() => {
    return topics.reduce((acc: Record<string, string>, topic) => {
      acc[topic.id] = topic.title;
      return acc;
    }, {} as Record<string, string>);
  }, [topics]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["trainingVersions", trainingId],
    queryFn: async () => {
      return await getAllTrainingVersionsUseCase.exec(trainingId!);
    },
    enabled: !!trainingId,
  });

  const handleReorder = useCallback(
    async (list: TrainingVersion[]) => {
      const success = await updateTrainingVersionOrderUseCase.exec(list);
      if (success) refetch();
    },
    [refetch]
  );

  const formConfig = useMemo(
    () => trainingVersionFormConfig(topicOptions),
    [topicOptions]
  );

  const selectedId =
    selected && "id" in selected && selected.id ? selected.id : undefined;

  return (
    <PageLayout isLoading={isLoading}>
      <DataTable
        title={t("trainingVersion.title")}
        datas={data || []}
        columns={trainingVersionTableConfig(t)}
        filters={["title", "description"]}
        onRowClicked={(row: TrainingVersion) => {
          setSelected(row);
        }}
        onAdd={
          permissions.canCreate
            ? () => setSelected(emptyTrainingVersion(trainingId!))
            : undefined
        }
        onReorder={permissions.canReorder ? handleReorder : undefined}
      />

      <Modal
        isOpen={!!selected}
        onClose={() => {
          setSelected(null);
        }}
        title={selectedId ? t("form.edit") : t("form.add")}
        actions={
          selectedId ? (
            <HistoryButton
              collection="trainingVersions"
              recordId={String(selectedId)}
              recordLabel={selected?.title}
              onRestored={() => {
                setSelected(null);
                refetch();
              }}
            />
          ) : undefined
        }
      >
        <Form
          className="md:w-[600px]"
          id={selectedId}
          onSave={async (
            formData: TrainingVersionFormData,
            id: string | number | null
          ) => {
            const merged: TrainingVersionFormData = {
              ...formData,
              trainingId: trainingId!,
              rank:
                formData.rank ??
                (selected && "rank" in selected ? selected.rank : undefined) ??
                0,
            };
            const success = await addUpdateTrainingVersionUseCase.exec(
              merged,
              id
            );
            if (success) {
              setSelected(null);
              refetch();
            }
          }}
          onDelete={async (rowId: string | number) => {
            const success = await deleteTrainingVersionUseCase.exec(rowId);
            if (success) {
              setSelected(null);
              refetch();
            }
          }}
          form={form}
          formConfig={formConfig}
          onCancel={() => setSelected(null)}
        />
      </Modal>
    </PageLayout>
  );
}
